import { z } from 'zod';
import { ResponseUnmaskStateSchema } from '~/features/shared/market-content/responseUnmask.schemas';
import { listingSubspecialtySchema } from '~/features/types';

export const ProviderJobResponseStatusSchema = z.enum([
  'MESSAGED',
  'APPLIED',
  'NEGOTIATING',
  'PENDING_ACCEPTANCE',
  'ACCEPTED',
  'REJECTED',
  'WITHDRAWN',
]);

export const ProviderJobResponseFilterSchema = z.enum(['all', 'active', 'accepted']);

export const ProviderJobResponseTurnSchema = z.enum(['YOUR_TURN', 'THEIR_TURN']).nullable();

export const ProviderJobResponseActionSchema = z.enum([
  'accept',
  'accept_listing',
  'counter',
  'withdraw',
  'reject',
]);

type ProviderJobResponseAction = z.infer<typeof ProviderJobResponseActionSchema>;

const warnedUnknownActions = new Set<string>();

// The API deploys several minutes before the client and open tabs keep running
// the old bundle, so an action this client does not know yet must not fail the
// whole list. Keep the known actions, warn once per unknown value, never throw.
const ProviderJobResponseAvailableActionsSchema = z
  .array(z.string())
  .default([])
  .transform((actions) =>
    actions.filter((action): action is ProviderJobResponseAction => {
      if (ProviderJobResponseActionSchema.safeParse(action).success) return true;
      if (!warnedUnknownActions.has(action)) {
        warnedUnknownActions.add(action);
        console.warn(`Ignoring unknown job response action "${action}".`);
      }
      return false;
    })
  );

export const ProviderJobNegotiationSenderSchema = z.enum(['PROVIDER', 'EMPLOYER']);

export const ProviderJobLatestNegotiationSchema = z.object({
  id: z.number().int().positive(),
  version: z.number(),
  sender: ProviderJobNegotiationSenderSchema,
  pricePerHour: z.string().nullable(),
  numOfShifts: z.number().nullable(),
  totalContractValue: z.string().nullable(),
});

export const ProviderJobListingSchema = z.object({
  id: z.number(),
  jobTitle: z.string(),
  jobType: z.string(),
  specialtyName: z.string().nullable(),
  generalSpecialtyFlag: z.boolean().optional(),
  subspecialties: z.array(listingSubspecialtySchema).optional(),
  workLocation: z.string(),
  stateCodes: z.array(z.string()),
  pricePerHour: z.string().nullable(),
  numOfShifts: z.number().nullable(),
  hoursPerShift: z.string().nullable(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
});

export const ProviderJobProviderSummarySchema = z.object({
  providerId: z.number().nullable(),
  responseId: z.number(),
  displayName: z.string(),
  masked: z.boolean(),
  providerCategory: z.string().nullable(),
  providerType: z.string().nullable(),
  verificationLevel: z.number().nullable(),
  npiNum: z.string().nullable().optional(),
});

export const ProviderJobUnmaskStateSchema = ResponseUnmaskStateSchema;

export const ProviderJobResponseSchema = z.object({
  responseId: z.number(),
  status: ProviderJobResponseStatusSchema,
  isUnmasked: z.boolean(),
  updatedAt: z.string().nullable(),
  appliedAt: z.string().nullable(),
  viewedAt: z.string().nullable(),
  listing: ProviderJobListingSchema,
  provider: ProviderJobProviderSummarySchema,
  unmaskState: ProviderJobUnmaskStateSchema,
  latestNegotiation: ProviderJobLatestNegotiationSchema.nullable(),
  negotiationCount: z.number().default(0),
  turn: ProviderJobResponseTurnSchema,
  availableActions: ProviderJobResponseAvailableActionsSchema,
});

export const ProviderJobMessageSchema = z.object({
  id: z.number(),
  responseId: z.number(),
  messageBody: z.string(),
  senderProviderId: z.number().nullable(),
  senderEmployerId: z.number().nullable(),
  senderUserId: z.number(),
  negotiationId: z.number().nullable(),
  negotiationVersion: z.number().nullable(),
  createdAt: z.string(),
});

export const ProviderJobNegotiationChainRowSchema = z
  .object({
    id: z.number().optional(),
    version: z.number().nullable().optional(),
    sender: ProviderJobNegotiationSenderSchema.nullable().optional(),
    numOfShifts: z.number().nullable().optional(),
    pricePerHour: z.string().nullable().optional(),
    totalContractValue: z.string().nullable().optional(),
    createdAt: z.string().nullable().optional(),
  })
  .passthrough();
